/* global globalThis */
// One authority and one state machine for every live view. No offline edit queue.
export function createQueueSync({
  eventId, fetchSnapshot, fetchQueue, fetchRevision, openSocket, onSnapshot, onStatus,
  deviceId, operationId, now = () => globalThis.performance.now(), timers = globalThis,
  environment = globalThis.window, visibility = globalThis.document,
}) {
  let snapshot = null;
  let lastAppliedRevision = -1;
  let targetRevision = -1;
  let verifiedAt = null;
  let connected = false;
  let stopped = false;
  let epoch = 0;
  let status = "SYNCING";
  let updating = false; // Presentation only; SYNCING still blocks operations.
  let socket = null;
  let reconnectTimer = null;
  let pollTimer = null;
  let syncing = null;
  let fullRequested = false;
  let fullRetryRequired = false;
  let pending = null;
  let sending = false;
  let operationNotice = "";
  const maxAge = 12000;

  function report(next, reason = "", routine = false) {
    status = next;
    updating = status === "SYNCING" && routine;
    onStatus({ status, updating, reason, notice: operationNotice, lastAppliedRevision, pending: Boolean(pending) });
  }
  function revisionOf(value) {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }
  function apply(incoming) {
    if (incoming?.eventId && incoming.eventId !== eventId) return false;
    if (incoming?.event?.eventId && incoming.event.eventId !== eventId) return false;
    const revision = revisionOf(incoming?.queue?.revision);
    if (revision === null || revision < lastAppliedRevision) return false;
    snapshot = { ...snapshot, ...incoming };
    lastAppliedRevision = revision;
    targetRevision = Math.max(targetRevision, revision);
    onSnapshot(snapshot);
    return true;
  }
  function fresh() {
    return verifiedAt !== null && now() >= verifiedAt && now() - verifiedAt < maxAge;
  }
  function ready() {
    return !stopped && connected && status === "LIVE" && !pending && !sending &&
      lastAppliedRevision >= targetRevision && fresh() &&
      visibility?.visibilityState !== "hidden";
  }
  function settle() {
    const catchingUp = !fresh() || visibility?.visibilityState === "hidden" ||
      fullRequested || fullRetryRequired || lastAppliedRevision < targetRevision;
    if (!connected) report("OFFLINE", "Connection lost. Showing the last confirmed state.");
    else if (catchingUp || pending) report("SYNCING", "Catching up with the ceremony.", updating && !catchingUp);
    else report("LIVE");
  }
  async function resync(full = false) {
    if (stopped) return;
    fullRequested = fullRequested || full;
    if (syncing) return syncing;
    const includeFull = !snapshot || fullRequested || fullRetryRequired;
    fullRequested = false;
    const generation = epoch;
    report(connected ? "SYNCING" : "OFFLINE", "Checking the current ceremony state.", updating && !includeFull);
    syncing = (async () => {
      try {
        const incoming = includeFull ? await fetchSnapshot() : await fetchQueue();
        if (stopped || generation !== epoch) return;
        if (incoming.syncProtocol !== 1 || !apply(incoming)) throw new Error("Invalid or older queue snapshot");
        if (includeFull) fullRetryRequired = false;
        verifiedAt = now();
        settle();
      } catch {
        if (!stopped && generation === epoch) {
          // Keep the full snapshot requirement, but retry on a later poll rather
          // than recursively retrying a failed fetch without a delay.
          if (includeFull) fullRetryRequired = true;
          report("OFFLINE", "Cannot verify the ceremony state. Controls are paused.");
        }
      }
    })();
    await syncing;
    syncing = null;
    if (fullRequested && connected && !stopped) { await resync(); return; }
    // A notification received during a read may name an even newer revision.
    // The next poll repairs it; never unlock controls against the older result.
    if (pending && connected && generation === epoch && fresh() && !fullRetryRequired) {
      void deliver().catch(() => {});
    }
  }
  async function poll() {
    if (stopped) return;
    if (!fresh()) report("SYNCING", "Checking connection and queue position.");
    const generation = epoch;
    const revisionAtStart = lastAppliedRevision;
    try {
      const head = await fetchRevision();
      if (stopped || generation !== epoch) return;
      const revision = revisionOf(head.queueRevision);
      if (head.eventId !== eventId || revision === null || head.syncProtocol !== 1 || revision < revisionAtStart) {
        throw new Error("Invalid authoritative queue revision");
      }
      // A command or resync may have verified newer state while this probe was
      // in flight. Its older result must neither disable us nor extend freshness.
      if (revision < lastAppliedRevision) return;
      targetRevision = Math.max(targetRevision, revision);
      if (lastAppliedRevision < targetRevision) report("SYNCING", "Catching up with the ceremony.");
      if (lastAppliedRevision < targetRevision || !snapshot || status !== "LIVE" || pending) {
        await resync();
      } else {
        verifiedAt = now();
        settle();
      }
    } catch {
      if (!stopped && generation === epoch) report("OFFLINE", "Cannot verify the ceremony state. Controls are paused.");
    }
  }
  function connect() {
    if (stopped) return;
    const generation = ++epoch;
    connected = false;
    verifiedAt = null;
    report("SYNCING", "Connecting and checking ceremony state.");
    const lost = () => {
      if (stopped || generation !== epoch) return;
      connected = false;
      ++epoch; // Invalidate HTTP responses started before the disconnect.
      report("OFFLINE", "Connection lost. Controls are paused.");
      socket?.close();
      reconnectTimer = timers.setTimeout(connect, 1500);
    };
    try {
      socket = openSocket({
        onOpen: () => {
          if (stopped || generation !== epoch) return;
          connected = true;
          void resync(true);
        },
        onMessage: (message) => {
          if (stopped || generation !== epoch || message.eventId !== eventId) return;
          if (message.type === "refresh") { void resync(true); return; }
          if (!["queue_delta", "lower_thirds_update"].includes(message.type)) return;
          const revision = revisionOf(message.queueRevision);
          if (revision === null) {
            report("SYNCING", "Checking the queue revision.");
            void resync(); return;
          }
          if (revision <= lastAppliedRevision) return;
          const routine = (status === "LIVE" || updating) && fresh() && revision === lastAppliedRevision + 1;
          targetRevision = Math.max(targetRevision, revision);
          report("SYNCING", revision > lastAppliedRevision + 1 ? "Missed updates. Resynchronising." : "Receiving the latest queue.", routine);
          // Notifications are invalidations. Fetch the full authoritative queue,
          // not a partial delta that leaves local history/order inconsistent.
          void resync();
        },
        onClose: lost,
        onError: lost,
      });
    } catch { lost(); }
  }
  async function deliver() {
    if (!pending || sending || stopped) return;
    sending = true;
    const operation = pending;
    operation.attempts = (operation.attempts || 0) + 1;
    try {
      const result = await operation.send(JSON.parse(JSON.stringify(operation.envelope)));
      if (stopped) return result;
      if (!result.operation || result.operation.id !== operation.envelope.operationId) {
        throw new Error("The server did not confirm the operation ID.");
      }
      pending = null;
      operationNotice = operation.attempts > 1 || result.operation.replayed ? "Your previous action was confirmed. Do not repeat it." : "";
      apply(result);
      report("SYNCING", "Confirming the latest ceremony state.", updating);
      await resync();
      return result;
    } catch (error) {
      if (stopped) throw error;
      if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 408 && error.response?.status !== 429) {
        pending = null;
        operationNotice = error.response?.data?.error || "Your action was rejected. Review the latest state before trying again.";
        if (error.response?.data?.latestQueue) apply({ queue: error.response.data.latestQueue });
        report("SYNCING", "Queue changed. Review the latest state before trying again.");
        await resync();
      } else {
        report("OFFLINE", "Last action is unconfirmed. Reconnecting will check it safely.");
      }
      throw error;
    } finally { sending = false; settleAfterSend(); }
  }
  function settleAfterSend() {
    if (!stopped && status !== "OFFLINE" && fresh()) settle();
  }
  async function runOperation(send, payload = {}) {
    if (!ready()) {
      if (status === "LIVE") void resync();
      throw new Error("Wait until this device is LIVE before operating the queue.");
    }
    // Browser queue edits must remain bound to the snapshot they were built from.
    // Never relabel an old list with a newer revision, including during retries.
    const prepared = typeof payload === "function"
      ? payload(JSON.parse(JSON.stringify(snapshot.queue))) : payload;
    const replacement = "items" in prepared || "history" in prepared;
    if ((replacement && !("baseRevision" in prepared)) ||
        ("baseRevision" in prepared && prepared.baseRevision !== lastAppliedRevision)) {
      void resync();
      throw new Error("Queue changed. Review the latest state and try your edit again.");
    }
    operationNotice = "";
    pending = { send, envelope: JSON.parse(JSON.stringify({
      ...prepared, baseRevision: lastAppliedRevision, deviceId, operationId: operationId(),
    })) };
    report("SYNCING", "Confirming your action.", true);
    return deliver();
  }
  function wake() {
    if (stopped) return;
    verifiedAt = null;
    ++epoch;
    connected = false;
    if (reconnectTimer) timers.clearTimeout(reconnectTimer);
    socket?.close();
    connect();
  }
  function hidden() {
    if (visibility?.visibilityState === "hidden") {
      verifiedAt = null;
      report("SYNCING", "Device paused. Reconciliation required.");
    } else wake();
  }
  return {
    ready, resync, poll, runOperation,
    start() {
      connect();
      pollTimer = timers.setInterval(() => { void poll(); }, 5000);
      environment?.addEventListener("online", wake);
      environment?.addEventListener("offline", wake);
      visibility?.addEventListener("visibilitychange", hidden);
    },
    stop() {
      stopped = true; ++epoch;
      timers.clearInterval(pollTimer); timers.clearTimeout(reconnectTimer);
      socket?.close();
      environment?.removeEventListener("online", wake);
      environment?.removeEventListener("offline", wake);
      visibility?.removeEventListener("visibilitychange", hidden);
    },
  };
}
